'use client';

import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import React, { createContext, useContext, ReactNode, useState, useCallback, useMemo, useRef, useEffect } from 'react';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';
import { ShowingTagDto } from '@cineamo/api/models/showing-tag/ShowingTagDto.types';
import {
    PROGRAM_SORTING_KEYS,
    ProgramSortingKey
} from '@cineamo/api/models/website-configuration/WebsiteConfiguration.types';
import { WebsiteProgramDateFilter } from '@cineamo/api/models/website-configuration/WebsiteProgramDateFilter.enum';

import { ActiveFilters, Filter, FilterGroup, ProgramFilters } from '@/types/filter/ActiveFilters.types';

import { activeFiltersToSearchParams, parseSearchParamsToActiveFilters } from '@/helpers/showing-filter-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';
import { useFutureShowings, getShowingFilters } from '@/hooks/useFutureShowings';
import useLocalStorage from '@cineamo/composables/src/hooks/useLocalStorage';

import { IconStarSolid16 } from '@cineamo/ui/src/icons/ic-starSolid';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { useCinemaRooms } from '@/providers/CinemaRoomProvider';
import { useShowingTags } from '@/providers/ShowingTagProvider';

import { useDomainConfiguration } from './DomainConfigurationProvider';

type ProgramFilterContextType = {
    showingTags: ShowingTagDto[];
    filterGroups: ProgramFilters;
    activeFilters: ActiveFilters;
    activeFiltersCount: number;
    setFilter: (filter: Filter, group: FilterGroup) => void;
    resetFilters: () => void;
    sorting: ProgramSortingKey;
    setSorting: (sorting: ProgramSortingKey) => void;
};

const ProgramFilterContext = createContext<ProgramFilterContextType | undefined>(undefined);

// Define filter constants outside the component to avoid dependency issues
const timeFilter: FilterGroup = {
    groupLabel: 'showtimes-filter',
    filters: [
        { name: 'program.showtimes-option.lt-18', properties: { endTime: '18' } },
        {
            name: 'program.showtimes-option.gt-18-lt-22',
            properties: {
                startTime: '18',
                endTime: '22'
            }
        },
        { name: 'program.showtimes-option.gt-22', properties: { startTime: '22' } }
    ]
};

const dimensionFilter: FilterGroup = {
    groupLabel: 'dimension-filter',
    filters: [
        { name: '2D', properties: { isThreeDimensional: 'false' } },
        { name: '3D', properties: { isThreeDimensional: 'true' } }
    ]
};

const languageFilter: FilterGroup = {
    groupLabel: 'language-filter',
    filters: [
        {
            name: 'OV',
            properties: {
                isOriginalLanguage: 'true',
                isSubtitled: 'false'
            }
        },
        {
            name: 'OmU',
            properties: {
                isOriginalLanguage: 'true',
                isSubtitled: 'true'
            }
        }
    ]
};

const technologyFilter: FilterGroup = {
    groupLabel: 'technology-filter',
    multiSelectable: false,
    filters: [
        { name: 'showtimes.showing-info.technology.imax', properties: { isImax: 'true' } },
        { name: 'showtimes.showing-info.technology.dolby-atmos', properties: { isDolbyAtmos: 'true' } },
        { name: 'showtimes.showing-info.technology.dolby-vision', properties: { isDolbyVision: 'true' } },
        { name: 'showtimes.showing-info.technology.dolby-cinema', properties: { isDolbyCinema: 'true' } },
        { name: 'showtimes.showing-info.technology.4-dx', properties: { is4DX: 'true' } },
        { name: 'showtimes.showing-info.technology.screen-x', properties: { isScreenX: 'true' } },
        { name: 'showtimes.showing-info.technology.hfr', properties: { isHFR: 'true' } },
        { name: 'showtimes.showing-info.technology.live', properties: { isLive: 'true' } },
        { name: 'showtimes.showing-info.technology.d-box', properties: { isDbox: 'true' } }
    ]
};

// Filters contents that are automatically tagged as newly released (see isMovieReleaseDateNew), applied
// against the cached content data rather than showing fields, since release date is content-level.
const newFilter: FilterGroup = {
    groupLabel: 'new-filter',
    filters: [{ name: 'common.new', properties: { isNew: 'true' } }]
};

// Accessibility filter group with two independently selectable options: GRETA (movie-level — audio
// description / closed caption / sound amplification, matched against the cached content data) and
// Wheelchair (room-level — the showing's cinema room has wheelchair spaces, matched against the
// showing's cinemaRoomId), see hasMovieAccessibilityFeatures and the cinemaRooms lookup below.
const accessibilityFilter: FilterGroup = {
    groupLabel: 'accessibility-filter',
    multiSelectable: true,
    filters: [
        { name: 'greta.accessibility', properties: { isGreta: 'true' } },
        { name: 'showing-info.wheelchair-accessible', properties: { isWheelchairAccessible: 'true' } }
    ]
};

type ProgramFilterProviderProps = {
    children: ReactNode;
    // Contents currently loaded for the program page — used to derive the dynamic genre filter options
    // (only genres actually present among them are offered), see genreFilterGroup below.
    contents?: CachedContentData[];
    /**
     * Raw, unfiltered `showings-future` response of the cinema, fetched on the server.
     * IMPORTANT for SSR: this provider is the first `useFutureShowings` consumer in the
     * tree, so it creates the shared query-cache entry — without initial data here,
     * React Query ignores the `initialData` of every consumer mounting later and the
     * whole page renders without showings on the server.
     */
    initialShowings?: ShowingDto[];
    initialShowingsUpdatedAt?: number;
};

const IGNORED_QUERY_PARAMS = ['date', 'sort'];

// LocalStorage key for storing user's sorting preference
const SORTING_STORAGE_KEY = 'cineamo_program_sorting';

export function ProgramFilterProvider({
    children,
    contents = [],
    initialShowings,
    initialShowingsUpdatedAt
}: ProgramFilterProviderProps) {
    const searchParams = useSearchParams();
    const { cinemaRooms } = useCinemaRooms();
    const domainConfiguration = useDomainConfiguration();
    const cinemaId = domainConfiguration.cinema.id;
    const locale = useLocale();
    const t = useTranslations();
    const { track } = useAnalytics();

    // Get the current selected date from URL. Without a `date` query parameter
    // the configured initial date filter applies (same logic as ProgramList).
    const selectedDate =
        searchParams.get('date') ||
        (domainConfiguration.websiteConfiguration?.initialProgramDateFilter === WebsiteProgramDateFilter.ALL
            ? 'all'
            : null);

    // Dynamically calculate default sorting based on current selected date
    const defaultCinemaSorting = useMemo(() => {
        const isAllDates = selectedDate === 'all';
        return isAllDates
            ? (domainConfiguration.websiteConfiguration?.defaultProgramAllSorting ?? 'showings-desc')
            : (domainConfiguration.websiteConfiguration?.defaultProgramDailySorting ?? 'date-asc');
    }, [
        selectedDate,
        domainConfiguration.websiteConfiguration?.defaultProgramAllSorting,
        domainConfiguration.websiteConfiguration?.defaultProgramDailySorting
    ]);

    // Use localStorage hook to persist sorting preference. Write-only on purpose, see the sorting
    // state below.
    const [, setStoredSorting] = useLocalStorage(SORTING_STORAGE_KEY, defaultCinemaSorting);

    // The layout fetches this cinema's showing tags (the ones with showings from today on)
    // server-side and hands them down through ShowingTagProvider — requesting the identical list
    // again from every browser only paged /showing-tags a second time (CIN-14426).
    const { showingTags } = useShowingTags();

    // Fetch all future showings for the cinema to validate filters
    const { data: allShowings } = useFutureShowings(
        cinemaId,
        {},
        null, // No server filters
        {
            staleTime: 5 * 60 * 1000,
            ...(initialShowings && {
                initialData: initialShowings,
                initialDataUpdatedAt: initialShowingsUpdatedAt
            })
        }
    );

    // Initialize state from search params on page load
    const [activeFilters, setActiveFilters] = useState<ActiveFilters>(() => {
        return parseSearchParamsToActiveFilters(searchParams);
    });

    // Track if user has explicitly set sorting (via URL param)
    const hasExplicitSorting = searchParams.has('sort');

    // Track if user has explicitly selected sorting in this session
    const [hasUserSelectedSorting, setHasUserSelectedSorting] = useState(false);

    // Initialize sorting from URL params or use default. The stored preference is deliberately not
    // read here: localStorage does not exist on the server, so seeding the state from it sorted the
    // list differently in the first client render than in the server HTML and hydration of the whole
    // program page failed. It also never survived mount — the effect below has reset the sorting to
    // the configured default since 7bc7d2b180 ("Fix changing the default sorting option when
    // selecting date"), which is the behaviour that has to stay (CIN-14426).
    const [sortingState, setSortingState] = useState<ProgramSortingKey>(() => {
        if (hasExplicitSorting) {
            // Priority 1: URL param
            const sortParam = searchParams.get('sort');
            return PROGRAM_SORTING_KEYS.includes(sortParam as ProgramSortingKey)
                ? (sortParam as ProgramSortingKey)
                : defaultCinemaSorting;
        }

        // Priority 2: Default based on date selection
        return defaultCinemaSorting;
    });

    // Update sorting when default changes (e.g., when switching between 'all' and specific dates)
    // but only if user hasn't explicitly set a sorting in URL or selected one in this session

    useEffect(() => {
        // If user has URL param or has explicitly selected sorting in this session, don't auto-change
        if (!hasExplicitSorting && !hasUserSelectedSorting) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: syncing derived sorting from URL/config
            setSortingState(defaultCinemaSorting);
        }
    }, [hasExplicitSorting, hasUserSelectedSorting, defaultCinemaSorting]);

    const isFilterActive = useCallback(
        (filter: Filter) => {
            // if filter matches queryString fully
            filter.isActive = Object.entries(filter.properties).every(([key, value]) => {
                const existingValue = activeFilters[key];
                if (!existingValue) return false;

                if (Array.isArray(existingValue) && typeof value === 'string') {
                    return existingValue.includes(value);
                } else {
                    return existingValue === value;
                }
            });
            return filter;
        },
        [activeFilters]
    );

    const hasMatchingShowing = useCallback(
        (filter: Filter): boolean => {
            // Show all filters while loading
            if (!allShowings.length) return true;

            // Convert filter properties to the format expected by getShowingFilters
            // Using Record type instead of any for type safety
            const query = filter.properties as Record<string, string | string[]>;
            const filterFunctions = getShowingFilters(query);

            // Check if at least one showing passes all filter conditions
            return allShowings.some((showing) => filterFunctions.every((fn) => fn(showing)));
        },
        [allShowings]
    );

    // Genre options are dynamic: only genres actually present among the currently loaded contents are
    // offered, translated the same way as everywhere else genres are shown (genres.tmdb-genre-{id}, see
    // getShowingInfo in showing-helper.tsx) and sorted alphabetically by that translated label.
    const genreFilterGroup: FilterGroup | false = useMemo(() => {
        const genreIds = new Set<string>();
        contents.forEach((content) => {
            content.genres?.forEach((genre) => {
                if (genre?.id !== undefined && genre?.id !== null) {
                    genreIds.add(genre.id.toString());
                }
            });
        });
        if (genreIds.size === 0) {
            return false;
        }
        return {
            groupLabel: 'genre-filter',
            multiSelectable: true,
            filters: Array.from(genreIds)
                .map((genreId) => ({
                    name: `genres.tmdb-genre-${genreId}`,
                    properties: { genreIds: genreId }
                }))
                .toSorted((a, b) => t(a.name).localeCompare(t(b.name)))
        };
    }, [contents, t]);

    // First, compute available filter groups based on showings (doesn't depend on activeFilters)
    const availableFilterGroups: FilterGroup[] = useMemo(
        () =>
            [
                timeFilter,
                dimensionFilter,
                languageFilter,
                technologyFilter,
                newFilter,
                accessibilityFilter,
                genreFilterGroup,
                showingTags.length && {
                    groupLabel: 'showing-tag-filter',
                    multiSelectable: true,
                    filters: (
                        [
                            {
                                name: (
                                    <span className="flex flex-row items-center gap-4">
                                        <IconStarSolid16 /> Highlight
                                    </span>
                                ),
                                properties: {
                                    isHighlighted: 'true'
                                }
                            }
                        ] as Filter[]
                    ).concat(
                        showingTags.map((showingTag) => ({
                            name: showingTag.name,
                            properties: {
                                showingTagIds: showingTag.id.toString()
                            }
                        }))
                    )
                },
                cinemaRooms.length && {
                    groupLabel: 'cinema-room-filter',
                    multiSelectable: true,
                    filters: cinemaRooms.map((cinemaRoom) => ({
                        name: cinemaRoom.name,
                        properties: {
                            cinemaRoomIds: cinemaRoom.id.toString()
                        }
                    }))
                }
            ]
                .filter((filterGroup): filterGroup is FilterGroup => Boolean(filterGroup))
                .map((filterGroup) => ({
                    ...filterGroup,
                    filters: filterGroup.filters.filter(hasMatchingShowing) // Filter out options with no matching showings
                }))
                .filter((group) => group.filters.length > 0), // Remove empty filter groups
        [cinemaRooms, showingTags, hasMatchingShowing, genreFilterGroup]
    );

    // Then, apply the active state to filters (depends on activeFilters)
    const filterGroups: FilterGroup[] = useMemo(
        () =>
            availableFilterGroups.map((filterGroup) => ({
                ...filterGroup,
                filters: filterGroup.filters.map(isFilterActive)
            })),
        [availableFilterGroups, isFilterActive]
    );

    function addPropertyToObj(propertyName: string, propertyValue: string, object: Record<string, string | string[]>) {
        // if property already exists convert its value to array
        if (propertyName in object) {
            const filterValuesArray = Array.isArray(object[propertyName])
                ? // or push to existing array
                  object[propertyName]
                : [object[propertyName]];

            object[propertyName] = filterValuesArray.concat(propertyValue);
        } else {
            object[propertyName] = propertyValue;
        }
    }

    function removePropertyFromObj(
        propertyName: string,
        propertyValue: string,
        object: Record<string, string | string[]>
    ) {
        let newFilterValue = [];
        if (Array.isArray(object[propertyName])) {
            // remove propertyValue from array and keep only all other values
            newFilterValue = object[propertyName].filter((f) => f !== propertyValue);
        }
        // if there are still values in the array, keep it
        if (newFilterValue.length) {
            object[propertyName] = newFilterValue;
        } else {
            delete object[propertyName];
        }
    }

    // filters are always synchronized with page URL query string
    // Store preserved params from initial load
    const preservedParamsRef = useRef<Record<string, string>>({});

    // Initialize preserved params once from initial search params
    useEffect(() => {
        const preservedParams: Record<string, string> = {};
        IGNORED_QUERY_PARAMS.forEach((param) => {
            const value = searchParams.get(param);
            if (value) {
                preservedParams[param] = value;
            }
        });
        preservedParamsRef.current = preservedParams;
    }, [searchParams]);

    const writeActiveFiltersToUrl = useCallback(
        (activeFilters: ActiveFilters, sortingValue?: ProgramSortingKey, explicitSorting?: boolean) => {
            const newSearchParams = activeFiltersToSearchParams(activeFilters, preservedParamsRef.current);

            // Add sorting to URL if provided or use current sorting
            const currentSorting = sortingValue ?? sortingState;

            // Only add to URL if:
            // 1. User explicitly set sorting (explicitSorting === true), OR
            // 2. Current sorting is different from default AND we already have it in URL
            if (explicitSorting || (currentSorting !== defaultCinemaSorting && hasExplicitSorting)) {
                newSearchParams.set('sort', currentSorting);
            } else {
                newSearchParams.delete('sort');
            }

            const newParamsStr = newSearchParams.toString();
            const newParams = newParamsStr ? `?${newParamsStr}` : '';
            if (newParams !== window.location.search) {
                window.history.replaceState(null, '', `${window.location.pathname}${newParams}`);
            }
        },
        [sortingState, defaultCinemaSorting, hasExplicitSorting]
    );

    const resetFilters = useCallback(() => {
        setActiveFilters({});
        writeActiveFiltersToUrl({}, sortingState);
    }, [writeActiveFiltersToUrl, sortingState]);

    /* eslint-disable react-hooks/preserve-manual-memoization -- track() values are read at invocation time, not render time */
    const setFilter = useCallback(
        (filter: Filter, group: FilterGroup) => {
            track('Program Filter Changed', {
                filter: filter.name.toString(),
                locale: locale
            });
            setActiveFilters((currentFilters) => {
                const newFilters = { ...currentFilters };

                // Check if filter is currently active based on current state
                const isCurrentlyActive = Object.entries(filter.properties).every(([key, value]) => {
                    const existingValue = currentFilters[key];
                    if (!existingValue) return false;

                    if (Array.isArray(existingValue) && typeof value === 'string') {
                        return existingValue.includes(value);
                    } else {
                        return existingValue === value;
                    }
                });

                if (!group.multiSelectable) {
                    // clear all other filters from this group first
                    group.filters.forEach((groupFilter) => {
                        Object.entries(groupFilter.properties).forEach(([filterProperty, filterValue]) => {
                            if (groupFilter !== filter) {
                                removePropertyFromObj(filterProperty, filterValue as string, newFilters);
                            }
                        });
                    });
                }

                // manage all properties from selected filter
                Object.entries(filter.properties).forEach(([filterProperty, filterValue]) => {
                    if (isCurrentlyActive) {
                        // clear if it was selected
                        removePropertyFromObj(filterProperty, filterValue as string, newFilters);
                    } else {
                        // add if it wasn't
                        addPropertyToObj(filterProperty, filterValue as string, newFilters);
                    }
                });
                writeActiveFiltersToUrl(newFilters, sortingState);
                return newFilters;
            });
        },
        [writeActiveFiltersToUrl, sortingState, track]
    );
    /* eslint-enable react-hooks/preserve-manual-memoization */

    const activeFiltersCount = useMemo<number>(
        () =>
            filterGroups
                .map(
                    (filterGroup) =>
                        // get number of filters that are active
                        filterGroup.filters.filter((filter) => filter.isActive).length
                )
                // sum results
                .reduce((a, b) => a + b, 0),
        [filterGroups]
    );

    // Create setSorting function that updates both state, URL, and localStorage
    /* eslint-disable react-hooks/preserve-manual-memoization -- track() values are read at invocation time, not render time */
    const setSorting = useCallback(
        (newSorting: ProgramSortingKey) => {
            track('Program Sorting Changed', {
                sorting: newSorting,
                locale: locale
            });
            setSortingState(newSorting);

            // Mark that user has explicitly selected sorting in this session
            setHasUserSelectedSorting(true);

            // Save to localStorage for future visits using the hook
            setStoredSorting(newSorting);

            // Mark as explicit user action (true as third parameter)
            writeActiveFiltersToUrl(activeFilters, newSorting, true);
        },
        [activeFilters, writeActiveFiltersToUrl, setStoredSorting, track]
    );
    /* eslint-enable react-hooks/preserve-manual-memoization */

    const contextValue = useMemo(
        () => ({
            showingTags,
            filterGroups,
            activeFilters,
            activeFiltersCount,
            setFilter,
            resetFilters,
            sorting: sortingState,
            setSorting
        }),
        [
            showingTags,
            filterGroups,
            activeFilters,
            activeFiltersCount,
            setFilter,
            resetFilters,
            sortingState,
            setSorting
        ]
    );

    return <ProgramFilterContext.Provider value={contextValue}>{children}</ProgramFilterContext.Provider>;
}

export function useProgramFilterContext() {
    const context = useContext(ProgramFilterContext);
    if (context === undefined) {
        throw new Error('useProgramFilterContext must be used within a ProgramFilterProvider');
    }
    return context;
}
